import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const NavContainer = styled(motion.nav)<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.fixed};
  padding: 1.25rem 2rem;
  transition: all ${({ theme }) => theme.animations.duration.normal}
    ${({ theme }) => theme.animations.easing.ease};

  ${({ scrolled, theme }) =>
    scrolled
      ? `
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(30px);
    border-bottom: 1px solid ${theme.colors.surface.border};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 1rem 2rem;
  `
      : `
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem 1.5rem;
    ${({ scrolled }) =>
      scrolled &&
      `
      padding: 0.75rem 1.5rem;
    `}
  }
`;

const NavContent = styled.div`
  max-width: ${({ theme }) => theme.container["2xl"]};
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 50px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  transition: all ${({ theme }) => theme.animations.duration.normal}
    ${({ theme }) => theme.animations.easing.ease};
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  position: relative;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(220, 38, 38, 0.1);

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 12px 35px rgba(220, 38, 38, 0.2);
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(220, 38, 38, 0.3);
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
  }
`;

const LogoImage = styled(motion.img)`
  height: 72px;
  width: auto;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15));
  transition: all ${({ theme }) => theme.animations.duration.normal}
    ${({ theme }) => theme.animations.easing.ease};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 56px;
  }

  ${Logo}:hover & {
    filter: drop-shadow(0 8px 16px rgba(220, 38, 38, 0.25));
  }
`;

const LogoText = styled(motion.span)`
  font-family: ${({ theme }) => theme.typography.fonts.heading};
  font-size: ${({ theme }) => theme.typography.sizes["2xl"]};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.025em;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.sizes.xl};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent.primary};
    transition: width ${({ theme }) => theme.animations.duration.normal}
      ${({ theme }) => theme.animations.easing.ease};
  }

  ${Logo}:hover &::after {
    width: 100%;
  }
`;

const DesktopNav = styled.ul`
  display: flex;
  align-items: center;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ isActive: boolean }>`
  position: relative;
  padding: 0.75rem 1.25rem;
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  text-decoration: none;
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.accent.primary : theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sizes.base};
  min-height: 44px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.colors.accent.primary};
    background-color: ${({ theme }) => theme.colors.surface.hover};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 50%;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent.primary};
    transition: all ${({ theme }) => theme.animations.duration.fast}
      ${({ theme }) => theme.animations.easing.ease};
    transform: translateX(-50%);
  }

  &:hover::after,
  ${({ isActive }) =>
      isActive &&
      `
    &::after {
      width: 100%;
    }
  `}
    &:hover::after {
    width: 100%;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: none;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.hover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  padding: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3rem;
  padding: 1rem 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.surface.border};
`;

const MobileNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavLink = styled(Link)<{ isActive: boolean }>`
  display: block;
  padding: 1.25rem 1rem;
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.accent.primary : theme.colors.text.primary};
  text-decoration: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  min-height: 56px;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 0.5rem;

  &:hover {
    color: ${({ theme }) => theme.colors.accent.primary};
    background-color: ${({ theme }) => theme.colors.surface.hover};
  }

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const MobileContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.25rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.base};
  margin-top: 2rem;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.md};
  min-height: 56px;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Portfolio", href: "/portfolio" },
    { label: "Resources", href: "/resources" },
    { label: "Contact", href: "/contact" },
  ];

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <>
      <NavContainer
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        scrolled={scrolled}
      >
        <NavContent>
          <Logo to="/">
            <motion.div
              style={{
                position: "relative",
                width: "72px",
                height: "72px",
                overflow: "hidden",
              }}
            >
              <motion.img
                src="/approved-logo-k.jpg"
                alt="Swintek Logo"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15))",
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
          </Logo>

          <DesktopNav>
            {navItems.map((item) => (
              <li key={item.href}>
                <NavLink to={item.href} isActive={isActive(item.href)}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </DesktopNav>

          <ContactButton to="/contact">Get Started</ContactButton>

          <MobileMenuButton onClick={toggleMobileMenu}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </NavContent>
      </NavContainer>

      {mobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0, x: "100%" }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: "100%" }}
          transition={{ duration: 0.3 }}
          isOpen={mobileMenuOpen}
        >
          <MobileMenuHeader>
            <Logo to="/" onClick={() => setMobileMenuOpen(false)}>
              <motion.div
                style={{
                  position: "relative",
                  width: "56px",
                  height: "56px",
                  overflow: "hidden",
                }}
              >
                <motion.img
                  src="/approved-logo-k.jpg"
                  alt="Swintek Logo"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter: "drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15))",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
            </Logo>
            <MobileMenuButton onClick={toggleMobileMenu}>
              <X size={24} />
            </MobileMenuButton>
          </MobileMenuHeader>

          <MobileNavList>
            {navItems.map((item) => (
              <li key={item.href}>
                <MobileNavLink
                  to={item.href}
                  isActive={isActive(item.href)}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileNavLink>
              </li>
            ))}
          </MobileNavList>

          <MobileContactButton
            to="/contact"
            onClick={() => setMobileMenuOpen(false)}
          >
            Get Started
          </MobileContactButton>
        </MobileMenu>
      )}
    </>
  );
};

export default Navbar;
